import { AxiosResponse } from "axios";
import {
  AccountRepository,
  CreateAccountDTO,
} from "../../Domain/Repositories/AccountRepository";
import { Account } from "../../Model/Account";
import { SlackNotifySettings, SlackNotifySettingsUpdate } from "../../Model/SlackNotifySettings";
import { SlotOpsTimeSettings, SlotOpsTimeSettingsUpdate } from "../../Model/SlotOpsTimeSettings";
import { RankTrackerConfig, RankTrackerConfigUpdate } from "../../Model/RankTrackerConfig";
import { api } from '../../../../api/axiosInstance';

function normalizeList<T>(data: T[] | { results?: T[] }): T[] {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.results)) return data.results;
  return [];
}

export class AccountRepositoryImpl implements AccountRepository {
  private http = api;

  createAccount(dto: CreateAccountDTO): Promise<Account> {
    return this.http
      .post<Account>("/accounts/", dto)
      .then((r: AxiosResponse<Account>) => r.data);
  }

  getAccounts(): Promise<Account[]> {
    return this.http
      .get<Account[] | { results?: Account[] }>("/accounts/")
      .then((r: AxiosResponse<Account[] | { results?: Account[] }>) =>
        normalizeList(r.data),
      );
  }

  getAccount(id: number): Promise<Account> {
    return this.http
      .get<Account>(`/accounts/${id}/`)
      .then((r: AxiosResponse<Account>) => r.data);
  }

  updateAccount(id: number, dto: Partial<CreateAccountDTO>): Promise<Account> {
    return this.http
      .patch<Account>(`/accounts/${id}/`, dto)
      .then((r: AxiosResponse<Account>) => r.data);
  }

  deleteAccount(id: number): Promise<void> {
    return this.http.delete(`/accounts/${id}/`).then(() => undefined);
  }

  resetPassword(id: number, newPassword: string): Promise<void> {
    return this.http
      .post(`/accounts/reset-password/${id}/`, { new_password: newPassword })
      .then(() => {});
  }

  getSlackNotifySettings(): Promise<SlackNotifySettings> {
    return this.http
      .get<SlackNotifySettings>('/accounts/slack-notify-settings/')
      .then((r: AxiosResponse<SlackNotifySettings>) => r.data);
  }

  updateSlackNotifySettings(dto: SlackNotifySettingsUpdate): Promise<SlackNotifySettings> {
    return this.http
      .patch<SlackNotifySettings>('/accounts/slack-notify-settings/', dto)
      .then((r: AxiosResponse<SlackNotifySettings>) => r.data);
  }

  getSlotOpsTimeSettings(): Promise<SlotOpsTimeSettings> {
    return this.http
      .get<SlotOpsTimeSettings>('/accounts/slot-ops-time-settings/')
      .then((r: AxiosResponse<SlotOpsTimeSettings>) => r.data);
  }

  updateSlotOpsTimeSettings(dto: SlotOpsTimeSettingsUpdate): Promise<SlotOpsTimeSettings> {
    return this.http
      .patch<SlotOpsTimeSettings>('/accounts/slot-ops-time-settings/', dto)
      .then((r: AxiosResponse<SlotOpsTimeSettings>) => r.data);
  }

  getRankTrackerConfig(): Promise<RankTrackerConfig> {
    return this.http
      .get<RankTrackerConfig>('/settings/rank-tracker/')
      .then((r: AxiosResponse<RankTrackerConfig>) => r.data);
  }

  updateRankTrackerConfig(dto: RankTrackerConfigUpdate): Promise<RankTrackerConfig> {
    return this.http
      .patch<RankTrackerConfig>('/settings/rank-tracker/', dto)
      .then((r: AxiosResponse<RankTrackerConfig>) => r.data);
  }
}
